import { useEffect, useState } from 'react';
import * as anchor from '@project-serum/anchor';
import { useConnection, useAnchorWallet, useWallet } from '@solana/wallet-adapter-react';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

import { awaitTransactionSignatureConfirmation, getCandyMachineState } from './candyMachine.js';
import MintButton from './MintButton.js';
import CatchMeChildren from './CatchMeChildren';

export default function MintInfo({ className, children }) {
  const [candyMachineId, setCandyMachineId] = useState();
  const [config, setConfig] = useState();
  const [connection, setConnection] = useState();
  const [treasury, setTreasury] = useState();

  // Derived from above state
  const [candyMachineState, setCandyMachineState] = useState();

  // Derived from candyMachineState
  const [candyMachine, setCandyMachine] = useState();
  const [available, setAvailable] = useState();
  const [redeemed, setRedeemed] = useState();
  const [remaining, setRemaining] = useState();
  const [startDate, setStartDate] = useState();
  const [timeRemaining, setTimeRemaining] = useState(Infinity);

  const wallet = useAnchorWallet();

  // Set our variables on first load
  useEffect(() => {
    setCandyMachineId(new anchor.web3.PublicKey(process.env.NEXT_PUBLIC_CANDY_MACHINE_ID));
    setConfig(new anchor.web3.PublicKey(process.env.NEXT_PUBLIC_CANDY_MACHINE_CONFIG));
    setConnection(new anchor.web3.Connection(process.env.NEXT_PUBLIC_SOLANA_RPC_HOST));
    setTreasury(new anchor.web3.PublicKey(process.env.NEXT_PUBLIC_TREASURY_ADDRESS));
  }, []);

  // Populate Candy Machine State
  useEffect(() => {
    const getState = async () => {
      const cms =
        wallet && candyMachineId && connection && (await getCandyMachineState(wallet, candyMachineId, connection));
      setCandyMachineState(cms);
    };
    getState();
  }, [candyMachineId, wallet, connection]);

  // Update the countdown clock
  useEffect(() => {
    const { goLiveDate: time } = candyMachineState || {};
    const timerInterval = setInterval(() => setTimeRemaining((time || Infinity) - Date.now()), 1000);
    return () => clearInterval(timerInterval);
  }, [candyMachineState]);

  // Update variables when state changes
  useEffect(() => {
    const { candyMachine: cm, itemsAvailable, itemsRedeemed, itemsRemaining, goLiveDate } = candyMachineState || {};

    setCandyMachine(cm);
    setAvailable(itemsAvailable);
    setRedeemed(itemsRedeemed);
    setRemaining(itemsRemaining);
    setStartDate(goLiveDate);
  }, [candyMachineState]);

  const refreshCandyMachineState = () => {
    (async () => {
      if (!(wallet && candyMachineId && connection)) {
        return;
      }
      const cms = await getCandyMachineState(wallet, candyMachineId, connection);
      setCandyMachineState(cms);
    })();
  };

  // Display Connect Wallet message if no candyMachineState
  if (!candyMachineState) {
    return (
      <div className="flex flex-col md:rounded-lg shadow-xl w-full items-center justify-center max-w-screen-md text-white p-4">
        <MintButton />
      </div>
    );
  }

  // Show the countdown and the mint button if candyMachineState
  return (
    <div className="flex flex-col md:rounded-lg shadow-xl w-full h-full items-center p-4">
      {children}
      <CatchMeChildren>
        <MintButton
          candyMachineState={candyMachineState}
          candyMachineId={candyMachineId}
          config={config}
          connection={connection}
          numRemaining={remaining}
          refreshFn={refreshCandyMachineState}
          timeRemaining={timeRemaining}
          treasury={treasury}
          wallet={wallet}
        />
      </CatchMeChildren>
    </div>
  );
}
